import { createServerFn } from '@tanstack/react-start'
import { getRequest } from '@tanstack/react-start/server'
import { z } from 'zod'
import { getEffectiveUser } from '@server/identity'
import { MESSAGE_FEEDBACK_REASON_TAGS } from '@shared/chat-types'

const loadAuth = async () => (await import('@server/auth')).auth
const loadChat = async () => import('@server/chat')
const loadChatGeneration = async () => import('@server/chat-generation')

export const CONVERSATION_ACCESS_DENIED_ERROR = 'CONVERSATION_ACCESS_DENIED'

export const getConversations = createServerFn({ method: 'GET' }).handler(
  async () => {
    const request = getRequest()
    const [auth, chat] = await Promise.all([loadAuth(), loadChat()])
    const session = await auth.api.getSession({ headers: request.headers })
    const effUser = await getEffectiveUser(
      session?.user ?? null,
      request.headers,
    )

    return await chat.listUserConversations(
      effUser.type === 'user' ? effUser.id : undefined,
      effUser.type === 'anonymous' ? effUser.id : undefined,
    )
  },
)

export const getMessages = createServerFn({ method: 'POST' })
  .inputValidator(z.object({ conversationId: z.string() }))
  .handler(async ({ data }) => {
    const request = getRequest()
    const [auth, chat] = await Promise.all([loadAuth(), loadChat()])
    const session = await auth.api.getSession({ headers: request.headers })
    const effUser = await getEffectiveUser(
      session?.user ?? null,
      request.headers,
    )

    console.log(`[SERVER] getMessages: convId=${data.conversationId}`)
    const messages = await chat.getConversationMessages(
      data.conversationId,
      effUser.type === 'user' ? effUser.id : undefined,
      effUser.type === 'anonymous' ? effUser.id : undefined,
    )
    if (!messages) {
      throw new Error(CONVERSATION_ACCESS_DENIED_ERROR)
    }
    return messages
  })

export const getActiveChatGeneration = createServerFn({ method: 'POST' })
  .inputValidator(z.object({ conversationId: z.string() }))
  .handler(async ({ data }) => {
    const request = getRequest()
    const [auth, chatGeneration] = await Promise.all([
      loadAuth(),
      loadChatGeneration(),
    ])
    const session = await auth.api.getSession({ headers: request.headers })
    const effUser = await getEffectiveUser(
      session?.user ?? null,
      request.headers,
    )
    return await chatGeneration.getActiveChatGenerationStateForConversation(
      data.conversationId,
      effUser.type === 'user' ? effUser.id : undefined,
      effUser.type === 'anonymous' ? effUser.id : undefined,
    )
  })

export const submitMessageFeedback = createServerFn({ method: 'POST' })
  .inputValidator(
    z.object({
      messageId: z.string().min(1),
      rating: z.enum(['positive', 'negative']),
      reasonTags: z
        .array(z.enum(MESSAGE_FEEDBACK_REASON_TAGS))
        .max(8)
        .default([]),
      note: z.string().max(2000).nullable().optional(),
    }),
  )
  .handler(async ({ data }) => {
    const request = getRequest()
    const [auth, chat] = await Promise.all([loadAuth(), loadChat()])
    const session = await auth.api.getSession({ headers: request.headers })
    const effUser = await getEffectiveUser(
      session?.user ?? null,
      request.headers,
    )

    const feedback = await chat.submitMessageFeedbackForOwner(
      data.messageId,
      data.rating,
      data.reasonTags,
      data.note,
      effUser.type === 'user' ? effUser.id : undefined,
      effUser.type === 'anonymous' ? effUser.id : undefined,
    )

    if (!feedback) {
      throw new Error(CONVERSATION_ACCESS_DENIED_ERROR)
    }

    return feedback
  })

export const getDiagramRequests = createServerFn({ method: 'POST' })
  .inputValidator(
    z.object({
      messageIds: z.array(z.string()).min(1),
    }),
  )
  .handler(async ({ data }) => {
    const request = getRequest()
    const [auth, chat] = await Promise.all([loadAuth(), loadChat()])
    const session = await auth.api.getSession({ headers: request.headers })
    const effUser = await getEffectiveUser(
      session?.user ?? null,
      request.headers,
    )

    return await chat.getDiagramRequestsForConversationMessages(
      data.messageIds,
      effUser.type === 'user' ? effUser.id : undefined,
      effUser.type === 'anonymous' ? effUser.id : undefined,
    )
  })

export const getTableRequests = createServerFn({ method: 'POST' })
  .inputValidator(
    z.object({
      messageIds: z.array(z.string()).min(1),
    }),
  )
  .handler(async ({ data }) => {
    const request = getRequest()
    const [auth, chat] = await Promise.all([loadAuth(), loadChat()])
    const session = await auth.api.getSession({ headers: request.headers })
    const effUser = await getEffectiveUser(
      session?.user ?? null,
      request.headers,
    )

    return await chat.getTableRequestsForConversationMessages(
      data.messageIds,
      effUser.type === 'user' ? effUser.id : undefined,
      effUser.type === 'anonymous' ? effUser.id : undefined,
    )
  })

export const deleteConversation = createServerFn({ method: 'POST' })
  .inputValidator(z.object({ conversationId: z.string() }))
  .handler(async ({ data }) => {
    const request = getRequest()
    const [auth, chat] = await Promise.all([loadAuth(), loadChat()])
    const session = await auth.api.getSession({ headers: request.headers })
    const effUser = await getEffectiveUser(
      session?.user ?? null,
      request.headers,
    )

    const deleted = await chat.softDeleteConversation(
      data.conversationId,
      effUser.type === 'user' ? effUser.id : undefined,
      effUser.type === 'anonymous' ? effUser.id : undefined,
    )

    if (!deleted) {
      throw new Error(CONVERSATION_ACCESS_DENIED_ERROR)
    }

    return { success: true, conversationId: data.conversationId }
  })

export const resolveSourceReferenceRequest = createServerFn({ method: 'POST' })
  .inputValidator(z.object({ requestId: z.string() }))
  .handler(async ({ data }) => {
    const request = getRequest()
    const [auth, chat] = await Promise.all([loadAuth(), loadChat()])
    const session = await auth.api.getSession({ headers: request.headers })
    const effUser = await getEffectiveUser(
      session?.user ?? null,
      request.headers,
    )

    const resolved = await chat.resolveSourceReferenceRequestForOwner(
      data.requestId,
      effUser.type === 'user' ? effUser.id : undefined,
      effUser.type === 'anonymous' ? effUser.id : undefined,
    )

    if (!resolved) {
      throw new Error(CONVERSATION_ACCESS_DENIED_ERROR)
    }

    return resolved
  })
